/* eslint-disable react-hooks/exhaustive-deps */

import { useFetcher, useLocation, resolvePath } from 'react-router-dom';
import { matchRoutes } from 'react-router';
import { useEffect, useRef } from 'react';
import { routes } from '@/routes';
import { HOMEPAGE } from '@/constants';

const DEBUG = process.env.NODE_ENV !== 'production';
const dbg = (...args: any[]) => {
	if (DEBUG) console.log('[prefetch]', ...args);
};

function normalizeBase(b?: string) {
	const raw = (b ?? '').trim();
	if (!raw || raw === '/') return '';
	const s = raw.startsWith('/') ? raw : `/${raw}`;
	return s.endsWith('/') ? s.slice(0, -1) : s;
}

// Quita el basename si está presente al principio del href
function stripBase(href: string, base: string) {
	if (!base) return href;
	return href.startsWith(base) ? href.slice(base.length) || '/' : href;
}

const BASE = normalizeBase(HOMEPAGE);

export function usePrefetch() {
	const fetcher = useFetcher();
	const loc = useLocation();
	const done = useRef(new Set<string>());
	const t = useRef<number | null>(null);

	const cancel = () => {
		if (t.current) {
			clearTimeout(t.current);
			t.current = null;
			dbg('cancel: timeout cleared');
		}
	};

	const prefetch = (to?: string) => {
		cancel();
		dbg('start', { to, from: loc.pathname });
		if (!to) return;
		if ((navigator as any).connection?.saveData) {
			dbg('skip: saveData=true');
			return;
		}

		// 1) resolver relativo -> pathname absoluto
		const p = resolvePath(to, loc.pathname);
		const href = p.pathname + (p.search || '');
		dbg('resolved', { href });

		// 2) evitar duplicados y prefetch de la misma página
		if (done.current.has(href)) {
			dbg('skip: duplicate', { href });
			return;
		}
		if (href === loc.pathname) {
			dbg('skip: same_page', { href });
			return;
		}

		// 3) Quitar basename para el match
		const hrefForMatch = stripBase(href, BASE);
		if (href !== hrefForMatch) dbg('basename stripped', { base: BASE, from: href, to: hrefForMatch });

		// 4) comprobar que existe una ruta y que tiene loader
		const matches = matchRoutes(routes, hrefForMatch);
		if (!matches) {
			dbg('skip: no_match', { href: hrefForMatch });
			return;
		}
		const leaf = matches[matches.length - 1]?.route;
		const leafIsReal = Boolean(leaf?.index) || typeof leaf?.path === 'string';
		if (!leafIsReal) {
			dbg('skip: only_pathless_wrapper_matched', { href: hrefForMatch });
			return;
		}

		// ✅ SOLO si el leaf tiene loader merece la pena fetcher.load
		if (!leaf?.loader) {
			dbg('skip: leaf_has_no_loader', { href: hrefForMatch });
			return;
		}
		// 5) lanzar prefetch con un ligero delay y flag opcional
		const delayMs = 120;
		t.current = window.setTimeout(() => {
			const url = hrefForMatch + (hrefForMatch.includes('?') ? '&' : '?') + '__prefetch=1';
			dbg('run', { url, delayMs });
			fetcher.load(url);
			done.current.add(hrefForMatch);
			t.current = null;
		}, delayMs);
	};

	useEffect(() => {
		if (fetcher.state !== 'idle') {
			dbg('fetcher.state', fetcher.state, { data: fetcher.data });
		}
	}, [fetcher.state]);

	return { prefetch, cancel };
}
